import { GetStaticProps } from "next";
import Layout from "../../components/layout";
import Link from "../../components/Link";
import { MwSmallIcon } from "../../components/v2/icons";
import CustomerStories from "../../components/v2/customer-stories";
import Testimonial from "../../components/v2/mw-carousel/testimonial";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import nextI18NextConfig from "../../next-i18next.config";
import "../../styles/scss/customer.module.scss";
import HomeFeaturedTestimonials, { HomeFeaturedTestimonial } from "../../components/home/home-featured-testimonials";
import ClientTabsTestimonials from "../../components/v2/mw-carousel/testimonial-with-tabs";
import { useState } from "react";
import Dialog from "../../components/v2/dialog";
import Image from "next/image";
import CallToActionDocsLink from "../../components/v2/blog/call-to-action-docs-link";
import ComparisonVideoTestimonial from "../../components/v2/comparison/comparison-video-testimonial";
import "../../components/v2/styles/_call-to-action-blog.module.scss";


export default function CaseStudyPage() {
  const { t } = useTranslation('common');
  const heroData = t('customers.hero', { returnObjects: true, defaultValue: {} }) as any;
  const trustData = t('customers.trust', { returnObjects: true, defaultValue: {} }) as any;
  const [testimonialVideoOpen, setTestimonialVideoOpen] = useState(false);
  const [testimonialVideoSrc, setTestimonialVideoSrc] = useState<string | null>(
    null
  );
  const [isNotVideo, setIsNotVideo] = useState(false);

  const handleTestimonialVideoOpen = (src: string, isNotVideo?: boolean) => {
    setTestimonialVideoSrc(src);
    setTestimonialVideoOpen(true);
    setIsNotVideo(isNotVideo);
  };

  const DEFAULT_TESTIMONIALS: HomeFeaturedTestimonial[] = [
    {
      id: "corgi",
      title: "Middleware reduced the time spent debugging and resolving issues by nearly 90%.",
      body:
        "What sets their AI apart is that it does not stop at detecting issues. It actually helps fix problems in production, and for engineering teams, that's been a real game changer. At Corgi, we rely on Middleware to monitor our entire infrastructure and application stack.",
      author: "Nico Laqua, CEO at Corgi",
      imageSrc: "/images/corgi-testimonial-mw.webp",
      imageAlt: "Nico Laqua, CEO at Corgi",
      videoSrc: "/images/corgi-testimonial-demo.mp4",
    },
    {
      id: "eragon",
      title: "We chose Middleware because of their cutting edge AI capabilities.",
      body:
        "As companies continue to scale and deploy agents on eragon, it's critical that we get notified of any bugs or any issues within milliseconds. We use Middleware's AI capabilities and observability platform to do that, so we can continue to focus on our customers.",
      author: "Josh Sirota, CEO at Eragon",
      imageSrc: "/images/eragon-testimonial-mw.webp",
      imageAlt: "Josh Sirota, CEO at Eragon",
      videoSrc: "/images/eragon-testimonial-mw.mp4",
    },
  ];

  return (
    <>
      <Layout
        isHomePage={false}
        pageTitle={"Middleware: Customer Success Stories"}
        ogImage={"/images/case-study/case-study-og.png"}
        uri={"/customers/"}
        metaTitle={"Middleware: Customer Success Stories"}
        metaDesc={
          "Explore real-world success stories and discover how Middleware's full-stack cloud observability platform have empowered businesses to thrive."
        }
        metaRobotsNoindex={"index"}
        metaRobotsNofollow={"follow"}
      >
        <div className={"customer-page"}>
          <section className={"hero-section customer-hero"}>
            <div className={"container"}>
              <div className={"d-flex"}>
                <div className={"left-block"}>
                  <h1>
                    {String(heroData?.titlePrefix ?? 'Customer')} <br />
                    <span className={"text-gradient"}>
                      {String(heroData?.titleHighlight ?? 'Success Stories')}
                    </span>
                  </h1>
                  <p>
                    {String(heroData?.description ?? '')}
                  </p>
                  <Link href={"#read-stories"} className={"ahref-btn primary"}>
                    {String(heroData?.cta ?? '')}
                  </Link>
                </div>
                <div className={"right-block"}>
                  <ComparisonVideoTestimonial
                    onVideoOpen={handleTestimonialVideoOpen}
                    items={DEFAULT_TESTIMONIALS}
                    aspectRatio={846 / 768}
                  />
                </div>
              </div>
            </div>
          </section>

          <div className={"customer-trust"}>
            <div className={"container"}>
              <div className={"section-tag testimonial pd-right"}>
                <MwSmallIcon /> {String(trustData?.sectionTag ?? '')}
              </div>
              <h2>{String(trustData?.titlePrefix ?? '')}<span className={"text-gradient"}>{String(trustData?.titleHighlight ?? '')}</span>{String(trustData?.titleSuffix ?? '')}</h2>

              <div className={"our-trusted"}>
                <div className={"d-flex"}>
                  <img
                    src={"/images/v2/customer-logos/corgi-logo.svg"}
                    alt={"Corgi"}
                    width={200}
                    height={200}
                    loading={"lazy"}
                  />
                  <img
                    src={"/images/v2/customer-logos/dr-reddy-logo.svg"}
                    alt={"Corgi"}
                    width={200}
                    height={200}
                    loading={"lazy"}
                  />
                  <img
                    src={"/images/v2/customer-logos/hoichoi.svg"}
                    alt={"hoichoi"}
                    width={200}
                    height={200}
                    loading={"lazy"}
                    className={"lg-img"}
                  />
                  <img
                    src={"/images/trusted-company/hotplate.svg"}
                    alt={"Hotplate"}
                    width={200}
                    height={200}
                    loading={"lazy"}
                  />
                  <img
                    src={"/images/v2/customer-logos/ceat.svg"}
                    alt={"Ceat"}
                    width={200}
                    className={"bj"}
                    height={200}
                    loading={"lazy"}
                  />
                  <img
                    src={"/images/v2/customer-logos/lee.svg"}
                    alt={"Lee"}
                    className={"lee"}
                    width={200}
                    height={200}
                    loading={"lazy"}
                  />

                  <img
                    src={"/images/v2/customer-logos/wrangler.svg"}
                    alt={"Wrangler"}
                    width={200}
                    className={"wrangler"}
                    height={200}
                    loading={"lazy"}
                  />

                  <img
                    src={"/images/v2/customer-logos/effigo-logo.svg"}
                    alt={"Wrangler"}
                    width={200}
                    className={"bj"}
                    height={200}
                    loading={"lazy"}
                  />

                  <img
                    src={"/images/v2/customer-logos/finfactor.svg"}
                    alt={"Wrangler"}
                    width={200}
                    height={200}
                    loading={"lazy"}
                  />

                  <img
                    src={"/images/v2/customer-logos/nimble.svg"}
                    alt={"Nimble"}
                    width={200}
                    height={200}
                    loading={"lazy"}
                  />

                  <img
                    src={"/images/v2/customer-logos/simplification.svg"}
                    alt={"Simplification"}
                    width={200}
                    height={200}
                    loading={"lazy"}
                    className="lg-img"
                  />

                  <img
                    src={"/images/v2/customer-logos/surface_lab.svg"}
                    alt={"Surface Lab"}
                    width={200}
                    height={200}
                    loading={"lazy"}
                  />

                  <img
                    src={"/images/v2/customer-logos/bolna.svg"}
                    alt={"Bolna"}
                    width={200}
                    height={200}
                    loading={"lazy"}
                    className="bj"
                  />

                  <img
                    src={"/images/v2/customer-logos/monetize-now.svg"}
                    alt={"Monetize Now"}
                    width={200}
                    height={200}
                    loading={"lazy"}
                    className="max-img"
                  />

                  <img
                    src={"/images/v2/customer-logos/attend.svg"}
                    alt={"Attend"}
                    width={200}
                    height={200}
                    loading={"lazy"}
                  />

                  <img
                    src={"/images/v2/customer-logos/hamming-ai.svg"}
                    alt={"Hamming AI"}
                    width={200}
                    height={200}
                    loading={"lazy"}
                  />

                  <img
                    src={"/images/v2/customer-logos/wellness-360.svg"}
                    alt={"Wellness 360"}
                    width={200}
                    height={200}
                    loading={"lazy"}
                  />

                  <img
                    src={"/images/v2/customer-logos/jets-com.svg"}
                    alt={"Jets.com"}
                    width={200}
                    height={200}
                    loading={"lazy"}
                  />

                  <img
                    src={"/images/v2/customer-logos/firstock.svg"}
                    alt={"Firstock"}
                    width={200}
                    height={200}
                    loading={"lazy"}
                  />

                  <img
                    src={"/images/v2/customer-logos/g-star.svg"}
                    alt={"G-Star"}
                    width={200}
                    height={200}
                    loading={"lazy"}
                  />

                  <img
                    src={"/images/v2/customer-logos/cluster.svg"}
                    alt={"Cluster"}
                    width={200}
                    height={200}
                    loading={"lazy"}
                  />

                  <img
                    src={"/images/trusted-company/revenium.svg"}
                    alt={"Revenium"}
                    width={200}
                    height={200}
                    loading={"lazy"}
                    className="sm"
                  />

                  <img
                    src={"/images/trusted-company/invopop.svg"}
                    alt={"Invopop"}
                    width={200}
                    height={200}
                    loading={"lazy"}
                  />

                  <img
                    src={"/images/trusted-company/finflex.svg"}
                    alt={"Finflex"}
                    width={200}
                    height={200}
                    loading={"lazy"}
                    className="md-img"
                  />
                  <img
                    src={"/images/trusted-company/cloudchipr.svg"}
                    alt={"Cloudchipr"}
                    width={200}
                    height={200}
                    loading={"lazy"}
                    className="wd-130"
                  />

                  <img
                    src={"/images/trusted-company/trademarkia.svg"}
                    alt={"Trademarkia"}
                    width={200}
                    height={200}
                    loading={"lazy"}
                  />

                  <img
                    src={"/images/trusted-company/firstlgnite.svg"}
                    alt={"FirstIgnite"}
                    width={200}
                    height={200}
                    loading={"lazy"}
                  />

                  <img
                    src={"/images/trusted-company/visitly.svg"}
                    alt={"Visitly"}
                    width={200}
                    height={200}
                    loading={"lazy"}
                  />

                  <img
                    src={"/images/trusted-company/streamoid.svg"}
                    alt={"Streamoid"}
                    width={92}
                    height={200}
                    loading={"lazy"}
                    className="max-img"
                  />

                  <img
                    src={"/images/trusted-company/brainboard.svg"}
                    alt={"Brainboard"}
                    width={200}
                    height={200}
                    loading={"lazy"}
                  />

                  <img
                    src={"/images/trusted-company/merge.svg"}
                    alt={"Merge"}
                    width={100}
                    height={200}
                    loading={"lazy"}
                    className="lg-img"
                  />

                  <img
                    src={"/images/trusted-company/cumberland.svg"}
                    alt={"Cumberland"}
                    width={200}
                    height={200}
                    loading={"lazy"}
                  />

                </div>
              </div>
            </div>
          </div>

          <section className="testimonial-section">
            <div className="container">
              <div className="section-tag testimonial">{t("testimonials.tag")} 👤</div>
              <h2>Why <span>Organizations</span> Trust Middleware </h2>
              <HomeFeaturedTestimonials
                onVideoOpen={handleTestimonialVideoOpen}
              />

              <ClientTabsTestimonials />
            </div>
          </section>

          <div id={"read-stories"}>
            <CustomerStories />
          </div>

          <section className={`call-to-action-blog`}>
            <div className={'container'}>
              <div className={'d-flex'}>
                <h2>Join our community to stay updated, chat & connect with us</h2>
                <div className={"cta-buttons"}>
                  <Link href={"https://middlewarepublic.slack.com/join/shared_invite/zt-39l76rpo6-ZNGfpA_4U_kYL6oTuYAgmQ"} target={"_blank"} className={"ahref-btn primary"}>Join on Slack</Link>
                  <Link href={'https://app.middleware.io/auth/register/'} className={"ahref-btn secondary"}>Get Started Free</Link>
                </div>
              </div>
            </div>
          </section>
        </div>
      </Layout>


      <Dialog
        isOpen={testimonialVideoOpen}
        position="center"
        onClose={() => {
          setTestimonialVideoOpen(false);
          setTestimonialVideoSrc(null);
        }}
        size={isNotVideo ? "sx" : "md"}
        verticalCenter={true}
      >
        <div style={{ textAlign: "center" }}>
          {testimonialVideoSrc && !isNotVideo ? (
            <video
              key={testimonialVideoSrc}
              playsInline
              preload="metadata"
              autoPlay
              width="100%"
              height={450}
              controls={true}
            >
              <source src={testimonialVideoSrc} type="video/mp4" />
            </video>
          ) : isNotVideo ? <Image alt={"Testimonials Middleware"} height={520} width={360} src={testimonialVideoSrc} /> : null}
        </div>
      </Dialog>
    </>
  );
}

export const getStaticProps: GetStaticProps = async (ctx) => {
  const translations = await serverSideTranslations(
    ctx?.locale ?? "en",
    ["common", "header", "footer", "home"],
    nextI18NextConfig,
  );
  return {
    props: {
      ...translations,
      pageTitle: "Middleware: Customer Success Stories",
      description:
        "Explore real-world success stories and discover how Middleware's full-stack cloud observability platform have empowered businesses to thrive.",
      ogImage: "/images/case-study/case-study-og.png",
      uri: "/customers/",
      metaTitle: "Middleware: Customer Success Stories",
      metaDesc:
        "Explore real-world success stories and discover how Middleware's full-stack cloud observability platform have empowered businesses to thrive.",
      metaRobotsNoindex: "index",
      metaRobotsNofollow: "follow",
    },
  };
};
